import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { PlusIcon } from "components/icons/PlusIcon";
import { getIn, useFormikContext } from "formik";
import { ReactNode, useEffect, useRef, useState } from "react";
import {
  components as reactSelectComponents,
  MenuListProps,
  MenuPlacement,
  MultiValue,
  SingleValue,
} from "react-select";
import GrayGoogle from "static/images/GrayGoogle.svg";
import { Countries } from "types/Models/countries";
import { debounce } from "utils/debouncing";
import {
  getCity,
  getCountry,
  getState,
  getStreetAddress,
  getStreetAddressLine2,
  getZipCode,
} from "./AddressComponentFieldValues";
import {
  CustomGoogleSuggestedOption,
  CustomSavedAddressOption,
} from "./AddressComponentOptions";

const countryNameToCode: Record<string, string> = {
  "United States": "us",
};

export type SavedAddressFields = {
  uuid: string;
  street_address: string;
  street_address_line_2: string | null;
  city: string;
  state: string;
  zipcode: string;
  country: string;
};

export const AddressActionButton = ({
  handleCreateManually,
}: {
  handleCreateManually: () => void;
}) => (
  <div className="t-py-1 t-flex t-items-center t-gap-1 t-flex-wrap">
    <div className="t-text-body-sm t-text-text-30">
      Type to search and auto-fetch the address via{" "}
    </div>
    <img src={GrayGoogle} alt="GrayGoogle" />
    <div className="t-text-body-sm t-text-text-30">or</div>
    <Button
      type="button"
      customType="link"
      size="small"
      onClick={handleCreateManually}
    >
      add address manually
    </Button>
  </div>
);

const OrDivider = () => (
  <div className="t-flex t-items-center t-gap-2">
    <div className="t-h-px t-flex-1 t-bg-neutral-10" />
    <span className="t-text-body-sm t-text-text-30">or</span>
    <div className="t-h-px t-flex-1 t-bg-neutral-10" />
  </div>
);

const AddManuallyLink = ({
  query,
  onClick,
  showIcon = false,
}: {
  query: string;
  onClick: () => void;
  showIcon?: boolean;
}) => (
  <Button
    type="button"
    customType="link"
    size="small"
    leftIcon={showIcon ? <PlusIcon size="12" /> : undefined}
    onClick={onClick}
  >
    Add &quot;{query}&quot; manually
  </Button>
);

const SuggestionsMenuList = ({
  children,
  ...props
}: MenuListProps<OptionData>) => (
  <reactSelectComponents.MenuList {...props}>
    {children}
    {/* @ts-expect-error — actions is a custom prop forwarded to Select, see Combobox */}
    {props.selectProps?.actions && (
      <div className="t-py-2 t-px-3 t-sticky t-bottom-0 t-bg-white">
        {/* @ts-expect-error — actions is a custom prop forwarded to Select, see Combobox */}
        {props.selectProps?.actions}
      </div>
    )}
  </reactSelectComponents.MenuList>
);

const AddressNoOptionsMessage = ({
  query,
  onEnterManually,
}: {
  query: string;
  onEnterManually: () => void;
}) => {
  if (!query) {
    return (
      <div className="t-p-3 t-px-2 t-text-body-sm t-text-text-30">
        Type an address to search...
      </div>
    );
  }

  return (
    <div className="t-p-3 t-px-2 t-flex t-items-center t-gap-1 t-flex-wrap">
      <span className="t-text-body-sm t-text-text-30">No address found.</span>
      <AddManuallyLink query={query} onClick={onEnterManually} />
    </div>
  );
};

type AddressAutocompleteProps<TSavedAddress extends SavedAddressFields> = {
  availableCountriesData: Countries[];
  formPrefix?: string;
  required?: boolean;
  label?: string | ReactNode;
  selectedAddressId?: string | null;
  savedAddresses: TSavedAddress[];
  getSavedAddressLabel: (address: TSavedAddress) => string;
  onAddressSelect: (address: TSavedAddress | null) => void;
  onCreateManually: () => void;
  field_prefix?: string;
  onEditAddress: (addressId: string) => void;
  hideClear?: boolean;
  allowedCountries?: string[];
  note?: ReactNode;
  error?: string;
  isDisabled?: boolean;
  size?: "small" | "regular";
};

export const AddressAutocomplete = <TSavedAddress extends SavedAddressFields>({
  availableCountriesData,
  formPrefix = "",
  required = true,
  label = "Address",
  selectedAddressId,
  savedAddresses: allSavedAddresses,
  getSavedAddressLabel,
  onAddressSelect,
  onCreateManually,
  field_prefix = "",
  onEditAddress,
  hideClear,
  allowedCountries,
  note,
  error: parentError,
  isDisabled = false,
  size = "small",
}: AddressAutocompleteProps<TSavedAddress>) => {
  const place = useMapsLibrary("places");
  const tokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(
    null
  );
  const [suggestedPlaces, setSuggestedPlaces] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { setFieldValue, setFieldTouched, errors, validateForm } =
    useFormikContext<{ street_address: string }>();
  const [defaultAddress, setDefaultAddress] = useState<TSavedAddress>();

  const allowedCountryCodes = allowedCountries?.map(
    (name) => countryNameToCode[name]
  );

  const savedAddresses = allSavedAddresses.filter((address) =>
    getSavedAddressLabel(address)
      .toLocaleLowerCase()
      .includes(search.toLocaleLowerCase())
  );

  useEffect(() => {
    if (place?.AutocompleteSessionToken) {
      tokenRef.current = new place.AutocompleteSessionToken();
    }
  }, [place?.AutocompleteSessionToken]);

  useEffect(() => {
    setDefaultAddress(
      savedAddresses.find((address) => address.uuid === selectedAddressId)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddressId, savedAddresses]);

  const onChange = async (newValue: string) => {
    if (!place) return;

    const request: Parameters<
      typeof place.AutocompleteSuggestion.fetchAutocompleteSuggestions
    >[0] = {
      input: newValue,
      language: "en-US",
      sessionToken: tokenRef.current ?? undefined,
    };

    if (allowedCountryCodes?.length) {
      request.includedRegionCodes = allowedCountryCodes;
    }

    try {
      setIsLoading(true);
      const { suggestions } =
        await place.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request
        );
      setSuggestedPlaces(suggestions);
    } catch {
      setSuggestedPlaces([]);
    }
    setIsLoading(false);
  };

  const debouncedOnChange = debounce(onChange);

  const handleValidateForm = () => {
    setTimeout(() => {
      validateForm();
    }, 100);
  };

  const clearFields = () => {
    setFieldValue(`${formPrefix}${field_prefix}city`, "");
    setFieldValue(`${formPrefix}${field_prefix}country`, "");
    setFieldValue(`${formPrefix}${field_prefix}state`, "");
    setFieldValue(`${formPrefix}${field_prefix}zipcode`, "");
    setFieldValue(`${formPrefix}${field_prefix}street_address`, "");
    setFieldValue(`${formPrefix}${field_prefix}street_address_line_2`, "");
    setFieldValue(`${formPrefix}${field_prefix}uuid`, "");
  };

  const onSelect = async (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    try {
      onCreateManually();
      const selected = option as SingleValue<OptionData>;

      if (!selected) {
        clearFields();
      }

      if (!(option instanceof Array) && place && selected?.value) {
        const selectedPlace = new place.Place({ id: selected.value });
        const placeInfo = await selectedPlace.fetchFields({
          fields: ["addressComponents"],
        });

        setFieldValue(
          `${formPrefix}${field_prefix}city`,
          getCity(placeInfo.place) ?? ""
        );
        setFieldValue(
          `${formPrefix}${field_prefix}state`,
          getState(placeInfo.place, availableCountriesData) ?? ""
        );
        setFieldValue(
          `${formPrefix}${field_prefix}country`,
          getCountry(placeInfo.place, availableCountriesData) ?? ""
        );
        setFieldValue(
          `${formPrefix}${field_prefix}zipcode`,
          getZipCode(placeInfo.place) ?? ""
        );
        setFieldValue(
          `${formPrefix}${field_prefix}street_address`,
          getStreetAddress(placeInfo.place) ?? ""
        );
        setFieldValue(
          `${formPrefix}${field_prefix}street_address_line_2`,
          getStreetAddressLine2(placeInfo.place) ?? ""
        );
        setFieldValue(`${formPrefix}${field_prefix}uuid`, "");

        setFieldTouched(`${formPrefix}${field_prefix}country`, true, false);
        setFieldTouched(`${formPrefix}${field_prefix}state`, true, false);
      }

      handleValidateForm();
    } catch {}
  };

  const onSavedAddressSelect = async (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    try {
      const selectedAddress = savedAddresses.find(
        (address) => address.uuid === (option as SingleValue<OptionData>)?.value
      );

      onAddressSelect(selectedAddress ?? null);

      setFieldValue(
        `${formPrefix}${field_prefix}city`,
        selectedAddress?.city || ""
      );
      setFieldValue(
        `${formPrefix}${field_prefix}country`,
        selectedAddress?.country || ""
      );
      setFieldValue(
        `${formPrefix}${field_prefix}state`,
        selectedAddress?.state || ""
      );
      setFieldValue(
        `${formPrefix}${field_prefix}zipcode`,
        selectedAddress?.zipcode || ""
      );
      setFieldValue(
        `${formPrefix}${field_prefix}street_address`,
        selectedAddress?.street_address || ""
      );
      setFieldValue(
        `${formPrefix}${field_prefix}street_address_line_2`,
        selectedAddress?.street_address_line_2 || ""
      );
      setFieldValue(`${formPrefix}${field_prefix}uuid`, selectedAddress?.uuid);

      handleValidateForm();
    } catch {}
  };

  const handleCreateManually = () => {
    clearFields();
    onCreateManually();
  };

  const addressError = getIn(errors, `${formPrefix}street_address`);
  const displayError = parentError || addressError;
  const savedAddressesLength = savedAddresses.length;
  const comboboxType = displayError ? "error" : undefined;
  const clearComponent = hideClear ? { ClearIndicator: () => null } : {};

  const suggestedOptions = suggestedPlaces
    .map((suggestion) => ({
      value: suggestion.placePrediction?.toPlace().id,
      label: suggestion.placePrediction?.text.text,
    }))
    .filter(({ value }) => value);

  const hasSuggestions = suggestedOptions.length > 0;

  const commonComboboxProps = {
    isLoading,
    required,
    label,
    size,
    onInputChange: (newValue: string) => {
      setSearch(newValue);
      void debouncedOnChange(newValue);
    },
    placeholder: isMenuOpen ? "" : "Type an address to search...",
    onMenuOpen: () => setIsMenuOpen(true),
    onMenuClose: () => {
      setIsMenuOpen(false);
      setSuggestedPlaces([]);
    },
    menuPortalTarget: document.body,
    menuPlacement: "auto" as MenuPlacement,
    minMenuHeight: 400,
  };

  return (
    <div className="autocomplete-container">
      {savedAddressesLength > 0 ? (
        <Combobox
          onChange={onSavedAddressSelect}
          options={savedAddresses.map((address) => ({
            value: address.uuid,
            label: getSavedAddressLabel(address),
          }))}
          value={
            defaultAddress
              ? {
                  value: defaultAddress.uuid,
                  label: getSavedAddressLabel(defaultAddress),
                }
              : null
          }
          components={{
            Option: (props) => (
              <CustomSavedAddressOption
                {...props}
                editAddress={() =>
                  onEditAddress((props.data as OptionData).value)
                }
              />
            ),
            ...clearComponent,
          }}
          {...commonComboboxProps}
          shouldWrapText
          allowCopy
          allowEdit
          handleEdit={(selectedOption) => onEditAddress(selectedOption.value)}
          actions={
            <AddressActionButton handleCreateManually={handleCreateManually} />
          }
          type={comboboxType}
          isDisabled={isDisabled}
        />
      ) : (
        <Combobox
          actions={
            hasSuggestions ? (
              <div className="t-flex t-flex-col t-gap-2 t-py-1">
                <OrDivider />
                <AddManuallyLink
                  query={search}
                  onClick={handleCreateManually}
                  showIcon
                />
              </div>
            ) : undefined
          }
          onChange={onSelect}
          filterOption={() => true}
          // @ts-expect-error — option values are place ids
          options={suggestedOptions}
          type={comboboxType}
          components={{
            Option: (props) => <CustomGoogleSuggestedOption {...props} />,
            NoOptionsMessage: () => (
              <AddressNoOptionsMessage
                query={search}
                onEnterManually={handleCreateManually}
              />
            ),
            MenuList: SuggestionsMenuList,
          }}
          isDisabled={isDisabled}
          {...commonComboboxProps}
        />
      )}

      {parentError ? (
        <div className="t-mt-1.5 t-text-body-sm t-text-red t-italic t-font-light">
          {parentError}
        </div>
      ) : (
        addressError &&
        savedAddressesLength === 0 && (
          <div className="t-mt-1.5 t-text-body-sm t-text-red t-italic t-font-light">
            {addressError}
          </div>
        )
      )}

      {note && (
        <div className="t-mt-1" dangerouslySetInnerHTML={{ __html: note }} />
      )}
    </div>
  );
};
